import { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DevicesIcon from '@mui/icons-material/Devices';
import WbTwilightIcon from '@mui/icons-material/WbTwilight';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';

const COMPARISON_ROWS = [
  { feature: 'מספר רכיבים',      others: '4–6 רכיבים',             inorai: '21+ רכיבים ייחודיים' },
  { feature: 'תבניות לרכיב',     others: 'תבנית אחת קבועה',         inorai: '3–7 תבניות לבחירה' },
  { feature: 'עדכון תוכן',       others: 'דורש גישה פיזית / הדרכה', inorai: 'מכל מקום, בדקות, בלי עזרה' },
  { feature: 'תזמון ושבת',       others: 'ידני — הגבאי זוכר לשנות', inorai: 'מעבר שבת אוטומטי, לוח שבועי' },
  { feature: 'רכיבים חדשים',     others: 'עדכון ידני / עלות נוספת', inorai: 'כל חידוש מגיע לכל מנוי — מיד' },
  { feature: 'היסטוריית גרסאות', others: 'לא קיים',                 inorai: '5 גרסאות לכל לוח' },
];

const DIFFERENTIATORS = [
  {
    Icon: TrendingUpIcon,
    title: 'מתפתחת ללא הפסקה',
    desc: 'כל רכיב, תבנית ויכולת שנוספו — מגיעים מיד לכל מנוי. מה שקנית היום יהיה טוב יותר בשבוע הבא, בלי לשלם ובלי לבקש.',
  },
  {
    Icon: DevicesIcon,
    title: 'שינוי מכל מקום',
    desc: 'להוסיף הכרזה, לשנות זמן, לעדכן פרנס — בדקות, מכל מכשיר. בלי הדרכה ובלי לחכות לאחרים.',
  },
  {
    Icon: WbTwilightIcon,
    title: 'הצג שמבין שבת',
    desc: 'ביום שישי הצג עובר לבד לתצוגת שבת. זמני הלכה מחושבים אוטומטית. הגבאי לא צריך לזכור ולא לעשות כלום.',
  },
  {
    Icon: DashboardCustomizeIcon,
    title: 'שליטה על כל פיקסל',
    desc: 'גרידה חופשית, עשרות תבניות, רב-מסכים עם תזמון שבועי — כל לוח נראה בדיוק כמו שרצית.',
  },
];

/* ── Differentiator card ── */
const DCard = ({ Icon, title, desc }) => (
  <Box
    sx={{
      p: '28px 28px 26px',
      borderRadius: 3,
      background: 'rgba(255,255,255,0.06)',
      backdropFilter: 'blur(18px)',
      WebkitBackdropFilter: 'blur(18px)',
      border: '1px solid rgba(255,255,255,0.12)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.2)',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      cursor: 'default',
      position: 'relative',
      overflow: 'hidden',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
      '&:hover': {
        transform: 'translateY(-6px)',
        boxShadow: '0 24px 48px rgba(0,0,0,0.35), 0 0 24px rgba(212,160,23,0.1)',
        borderColor: 'rgba(212,160,23,0.28)',
      },
      /* Gold top-edge accent */
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0, left: 0, right: 0,
        height: '2px',
        background: 'linear-gradient(90deg, transparent 0%, rgba(212,160,23,0.7) 40%, rgba(212,160,23,0.7) 60%, transparent 100%)',
      },
    }}
  >
    {/* Icon */}
    <Box sx={{
      width: 52, height: 52,
      borderRadius: '50%',
      background: 'radial-gradient(circle, rgba(212,160,23,0.18) 0%, rgba(212,160,23,0.06) 100%)',
      border: '1.5px solid rgba(212,160,23,0.32)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>
      <Icon sx={{ color: '#f0c040', fontSize: '1.35rem' }} />
    </Box>

    <Typography sx={{
      fontFamily: 'Secular One, sans-serif',
      color: 'white',
      fontSize: '1.05rem',
      lineHeight: 1.35,
    }}>
      {title}
    </Typography>

    {/* Thin gold separator */}
    <Box sx={{ width: 28, height: '1.5px', bgcolor: 'rgba(212,160,23,0.45)', borderRadius: 1, flexShrink: 0 }} />

    <Typography sx={{
      fontFamily: 'Heebo, sans-serif',
      color: 'rgba(255,255,255,0.6)',
      lineHeight: 1.8,
      fontSize: '0.9rem',
      flex: 1,
    }}>
      {desc}
    </Typography>
  </Box>
);

/* ── Section ── */
const ComparisonSection = () => {
  const tableRef = useRef(null);
  const [tableVisible, setTableVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTableVisible(true); },
      { threshold: 0.15 }
    );
    if (tableRef.current) observer.observe(tableRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <Box id="comparison" sx={{
      bgcolor: '#0e2035',
      py: { xs: 10, md: 14 },
      px: { xs: 3, md: 6 },
      position: 'relative',
      overflow: 'hidden',
    }}>

      {/* ── Background (hero style) ── */}
      {/* Base dot grid */}
      <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: 'radial-gradient(rgba(218,165,32,0.06) 1px, transparent 1px)',
        backgroundSize: '28px 28px',
      }} />
      {/* Sparkle group A */}
      <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: 'radial-gradient(rgba(218,165,32,0.22) 1.5px, transparent 1.5px)',
        backgroundSize: '112px 112px', backgroundPosition: '0 0',
        animation: 'heroDotTwinkle 2.9s ease-in-out infinite',
      }} />
      {/* Sparkle group B */}
      <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: 'radial-gradient(rgba(200,155,20,0.16) 1.5px, transparent 1.5px)',
        backgroundSize: '84px 84px', backgroundPosition: '14px 28px',
        animation: 'heroDotTwinkle 3.7s ease-in-out 1.3s infinite',
      }} />
      {/* Sparkle group C */}
      <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: 'radial-gradient(rgba(255,210,60,0.20) 1.5px, transparent 1.5px)',
        backgroundSize: '140px 112px', backgroundPosition: '42px 14px',
        animation: 'heroDotTwinkle 4.4s ease-in-out 2.5s infinite',
      }} />
      {/* Sparkle group D */}
      <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: 'radial-gradient(rgba(218,165,32,0.18) 1px, transparent 1px)',
        backgroundSize: '196px 140px', backgroundPosition: '70px 56px',
        animation: 'heroDotTwinkle 5.1s ease-in-out 0.7s infinite',
      }} />
      {/* Top-right radial glow */}
      <Box sx={{ position: 'absolute', top: 0, right: 0, width: '55%', height: '60%',
        background: 'radial-gradient(ellipse at top right, rgba(154,114,9,0.18) 0%, transparent 60%)',
        pointerEvents: 'none',
      }} />
      {/* Large orbital circle CW */}
      <Box sx={{ position: 'absolute', right: '-18%', top: '50%',
        transform: 'translateY(-50%)', width: '70vw', height: '70vw',
        borderRadius: '50%', border: '1px solid rgba(154,114,9,0.18)', pointerEvents: 'none',
      }}>
        <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%',
          background: 'conic-gradient(from 0deg, transparent 93%, rgba(154,114,9,0.1) 95%, rgba(218,165,32,0.5) 96.5%, rgba(255,230,80,0.9) 97.5%, rgba(218,165,32,0.5) 98.5%, rgba(154,114,9,0.1) 99.5%, transparent 100%)',
          WebkitMask: 'radial-gradient(circle closest-side at 50% 50%, transparent 97.5%, rgba(0,0,0,0.5) 98.5%, black 99.2%, black 100.2%, rgba(0,0,0,0.3) 101%)',
          mask:       'radial-gradient(circle closest-side at 50% 50%, transparent 97.5%, rgba(0,0,0,0.5) 98.5%, black 99.2%, black 100.2%, rgba(0,0,0,0.3) 101%)',
          animation: 'spinCW 26s linear infinite',
        }} />
      </Box>
      {/* Medium orbital circle CCW */}
      <Box sx={{ position: 'absolute', left: '-10%', top: '50%',
        transform: 'translateY(-50%)', width: '44vw', height: '44vw',
        borderRadius: '50%', border: '1px solid rgba(154,114,9,0.13)', pointerEvents: 'none',
      }}>
        <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%',
          background: 'conic-gradient(from 180deg, transparent 93%, rgba(154,114,9,0.08) 95%, rgba(218,165,32,0.4) 96.5%, rgba(255,230,80,0.75) 97.5%, rgba(218,165,32,0.4) 98.5%, rgba(154,114,9,0.08) 99.5%, transparent 100%)',
          WebkitMask: 'radial-gradient(circle closest-side at 50% 50%, transparent 97.5%, rgba(0,0,0,0.5) 98.5%, black 99.2%, black 100.2%, rgba(0,0,0,0.3) 101%)',
          mask:       'radial-gradient(circle closest-side at 50% 50%, transparent 97.5%, rgba(0,0,0,0.5) 98.5%, black 99.2%, black 100.2%, rgba(0,0,0,0.3) 101%)',
          animation: 'spinCCW 38s linear infinite',
        }} />
      </Box>

      {/* ── Content ── */}
      <Box sx={{ maxWidth: '1000px', mx: 'auto', position: 'relative', zIndex: 1 }}>

        {/* ── Header ── */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          {/* Label with side lines */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ flex: 1, maxWidth: 60, height: '1px', bgcolor: 'rgba(154,114,9,0.45)' }} />
            <Typography sx={{
              fontFamily: 'Heebo, sans-serif',
              color: '#9a7209',
              fontSize: '0.78rem',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              flexShrink: 0,
            }}>
              למה גבאים בוחרים ב־inorai
            </Typography>
            <Box sx={{ flex: 1, maxWidth: 60, height: '1px', bgcolor: 'rgba(154,114,9,0.45)' }} />
          </Box>

          <Typography
            component="h2"
            dir="rtl"
            sx={{
              fontFamily: 'Frank Ruhl Libre, serif',
              color: 'white',
              fontSize: { xs: '1.9rem', md: '2.5rem' },
              fontWeight: 900,
              letterSpacing: '-0.02em',
              mb: 2,
              direction: 'rtl',
            }}
          >
            לא עוד לוח שרק מציג -{' '}
            <Box component="span" className="lp-gold-text">מערכת שעובדת</Box>
          </Typography>

          <Typography sx={{
            color: 'rgba(255,255,255,0.58)',
            fontFamily: 'Heebo, sans-serif',
            fontSize: '1rem',
            maxWidth: '480px',
            mx: 'auto',
            lineHeight: 1.85,
          }}>
            פתרונות אחרים נותנים לוח סטטי שצריך לעדכן ידנית.
            <br />
            inorai חיה, מתפתחת ומשתנה — לבד.
          </Typography>
        </Box>

        {/* ── Comparison table ── */}
        <Box
          ref={tableRef}
          className={tableVisible ? 'lp-comp-visible' : ''}
          sx={{
            borderRadius: 3,
            border: '1px solid rgba(255,255,255,0.1)',
            overflow: 'hidden',
            mb: 8,
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            background: 'rgba(255,255,255,0.04)',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.07), 0 16px 40px rgba(0,0,0,0.25)',
          }}
        >
          {/* Table header */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: '1.1fr 1fr 1fr',
            bgcolor: 'rgba(0,0,0,0.22)',
            borderBottom: '1px solid rgba(255,255,255,0.07)',
          }}>
            <Box sx={{ p: '14px 16px' }}>
              <Typography sx={{
                fontFamily: 'Secular One, sans-serif',
                color: 'rgba(255,255,255,0.45)',
                fontSize: '0.82rem',
              }}>יכולת</Typography>
            </Box>
            <Box sx={{ p: '14px 16px', textAlign: 'center' }}>
              <Typography sx={{
                fontFamily: 'Secular One, sans-serif',
                color: 'rgba(255,255,255,0.38)',
                fontSize: '0.82rem',
              }}>פתרונות אחרים</Typography>
            </Box>
            {/* inorai header — visually crowned */}
            <Box sx={{
              p: '10px 16px',
              textAlign: 'center',
              bgcolor: 'rgba(212,160,23,0.07)',
              borderTop: '2.5px solid rgba(212,160,23,0.55)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Box sx={{
                display: 'inline-flex', alignItems: 'center',
                px: 1.5, py: 0.4,
                borderRadius: '6px',
                bgcolor: 'rgba(212,160,23,0.16)',
                border: '1px solid rgba(212,160,23,0.38)',
              }}>
                <Typography sx={{
                  fontFamily: 'Secular One, sans-serif',
                  color: '#d4a017',
                  fontSize: '0.82rem',
                  letterSpacing: '0.03em',
                }}>✦ inorai</Typography>
              </Box>
            </Box>
          </Box>

          {/* Rows */}
          {COMPARISON_ROWS.map((row, i) => (
            <Box
              key={i}
              className="lp-comp-row"
              sx={{
                display: 'grid',
                gridTemplateColumns: '1.1fr 1fr 1fr',
                borderBottom: i < COMPARISON_ROWS.length - 1 ? '1px solid rgba(255,255,255,0.045)' : 'none',
                '&:hover .comp-inorai-cell': { bgcolor: 'rgba(212,160,23,0.1)' },
              }}
            >
              {/* Feature */}
              <Box sx={{ p: '13px 16px', display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.62)', fontFamily: 'Heebo, sans-serif', fontSize: '0.88rem' }}>
                  {row.feature}
                </Typography>
              </Box>
              {/* Others */}
              <Box sx={{ p: '13px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography component="span" sx={{ color: 'rgba(255,255,255,0.22)', fontSize: '1rem', lineHeight: 1, fontWeight: 700 }}>✗</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.38)', fontFamily: 'Heebo, sans-serif', fontSize: '0.83rem', textAlign: 'center' }}>
                    {row.others}
                  </Typography>
                </Box>
              </Box>
              {/* inorai */}
              <Box
                className="comp-inorai-cell"
                sx={{ p: '13px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(212,160,23,0.055)', transition: 'background 0.2s' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography component="span" sx={{ color: '#d4a017', fontSize: '1rem', lineHeight: 1, fontWeight: 700 }}>✓</Typography>
                  <Typography sx={{ color: '#e0b830', fontFamily: 'Heebo, sans-serif', fontSize: '0.83rem', fontWeight: 600, textAlign: 'center' }}>
                    {row.inorai}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        {/* ── 4 differentiator cards — 2×2 grid ── */}
        <Box
          className={`lp-stagger-container ${tableVisible ? 'lp-visible' : ''}`}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: { xs: 2, md: 2.5 },
            alignItems: 'stretch',
          }}
        >
          {DIFFERENTIATORS.map((d, i) => (
            <Box key={i} className="lp-stagger-item" sx={{ display: 'flex' }}>
              <DCard Icon={d.Icon} title={d.title} desc={d.desc} />
            </Box>
          ))}
        </Box>

      </Box>
    </Box>
  );
};

export default ComparisonSection;
